'use client';
import { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { markHydrated } from './redux/slices/authSlice';
import {
  userProfileRequestSuccess,
  userProfileRequestStarted,
  userProfileRequestError,
} from './redux/slices/userProfileSlice';
import { useAppSockets } from '../hooks';
import { ThemeContextProvider, useThemeColors } from '../context/ThemeContext';
import LoadingScreen from '../components/LoadingScreen';
import { buildApiConfig } from '../services/api/apiClient';
import { endPointEnums } from '../services/api/endpoints';
import axios from 'axios';

// Import RouteProgress dynamically to avoid SSR issues with useSearchParams
const RouteProgress = dynamic(
  () => import('../kit').then((mod) => ({ default: mod.RouteProgress })),
  { ssr: false }
);

// Inner component that can access theme colors
function AuthHydratorInner({ children }) {
  const themeColors = useThemeColors();
  const isHydrated = useSelector((state) => state.auth?.isHydrated);

  // Show loading screen while hydrating
  if (!isHydrated) {
    return <LoadingScreen message="Loading..." />;
  }

  return (
    <>
      {children}
      <RouteProgress color={themeColors.primaryColor} />
    </>
  );
}

export default function AuthHydrator({ children }) {
  const dispatch = useDispatch();
  const fetchedRef = useRef(false);
  useAppSockets();

  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
  const userProfileData = useSelector((state) => state.userProfile?.data);

  // After PersistGate resolves (we're inside it), redux-persist has already
  // restored isLoggedIn / user / userType from persist:root.
  // We just need to flip isHydrated so loading screens & guards unblock.
  useEffect(() => {
    dispatch(markHydrated());
  }, [dispatch]);

  // Auto-fetch user profile when logged in but profile data is missing
  useEffect(() => {
    if (!isLoggedIn || userProfileData || fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchProfile = async () => {
      try {
        dispatch(userProfileRequestStarted());
        const config = buildApiConfig(endPointEnums.GET_USER_PROFILE);
        const response = await axios({
          url: config.endpoint.url,
          method: config.endpoint.method,
          headers: config.headers,
        });
        if (response.data) {
          dispatch(userProfileRequestSuccess(response.data?.data || response.data));
        }
      } catch (err) {
        console.warn('[AuthHydrator] Profile fetch failed:', err?.message);
        dispatch(userProfileRequestError(err?.message));
        fetchedRef.current = false; // allow retry
      }
    };

    fetchProfile();
  }, [isLoggedIn, userProfileData, dispatch]);

  return (
    <ThemeContextProvider>
      <AuthHydratorInner>{children}</AuthHydratorInner>
    </ThemeContextProvider>
  );
}
